import type { Category, Package } from '../components';
import { OrderByOptions } from '../orderby.enum';
import { StateController } from '../state';

const requestFields: Array<string> = [
  'iconDominantColor',
  'iconUrl',
  'awards',
  'isCertifiedToWorkOnUmbracoCloud',
  'isPromoted',
  'isPartner',
  'isHQ',
  'isHQSupported',
  'id',
  'title',
  'description',
  'packageId',
  'packageIdForProvidedUmbracoVersion',
  'packageType',
  'tags',
  'umbracoMajorVersionsSupported',
] satisfies (keyof Package)[];

export type QueryType =
  | 'popular'
  | 'recent'
  | 'promoted'
  | 'installs'
  | 'updated'
  | undefined;

export function CardQueryGenerator(type?: QueryType): object {
  const routeTo404 = () => {
    window.history.pushState({}, '', '/error/404');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const getCategoryProperty = (key: keyof Category) => {
    const category = StateController.current.categories?.find(
      x => x.link === window.location.pathname
    );

    return category?.[key];
  };

  const stateQuery = StateController.current.query;
  const stateQueryKey = StateController.current.queryKey;
  const stateFilter = StateController.current.filter;

  const query: { [key: string]: any; _queryType: QueryType } = {
    _queryType: type,
  };

  // if we have a key but no query, it's invalid and we should route back to home
  if (stateQueryKey && !stateQuery) {
    routeTo404();
  }

  if (type === 'promoted') {
    query.promotedOnly = true;
    query.orderBy = OrderByOptions.Installs;
  } else if (type === 'popular') {
    query.orderBy = OrderByOptions.Installs;
  } else if (type === 'recent') {
    query.orderBy = OrderByOptions.Created;
  } else if (type === 'updated') {
    query.orderBy = OrderByOptions.Updated;
  } else {
    let parsedQuery: string | undefined = stateQuery;

    if (StateController.isCategoryView) {
      parsedQuery = getCategoryProperty('id');
      if (!parsedQuery) routeTo404();
    }

    if (parsedQuery && stateQueryKey) {
      query[stateQueryKey] = parsedQuery;
    }
  }

  query.fields = requestFields.join(',');

  // ensure search defaults to relevance. OrderBy will be set on the state controller
  // but we fallback to relevance just in case
  if (query.text && !query.orderBy) {
    query.orderBy =
      StateController.current.filter?.orderBy ?? OrderByOptions.Relevance;
  }

  const result = { ...stateFilter, ...query };

  return result;
}
